import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SimpleEmailSend = () => {
    const { toast } = useToast();
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");

    const handleSend = async () => {
        if (!email || !subject || !message) {
            toast({
                title: "กรุณากรอกข้อมูลให้ครบ",
                description: "ต้องกรอกอีเมล์, หัวข้อ และข้อความ",
                variant: "destructive",
            });
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast({
                title: "รูปแบบอีเมล์ไม่ถูกต้อง",
                description: "กรุณาตรวจสอบอีเมล์อีกครั้ง",
                variant: "destructive",
            });
            return;
        }

        setSending(true);
        try {
            const { data, error } = await supabase.functions.invoke("send-simple-email", {
                body: {
                    to: email,
                    subject: subject,
                    message: message,
                },
            });

            if (error) throw error;

            if (!data.success) {
                throw new Error(data.error || "Failed to send email");
            }

            setSent(true);
            toast({
                title: "ส่งอีเมล์สำเร็จ!",
                description: `ส่งไปยัง ${email} เรียบร้อยแล้ว`,
            });

            // Reset form after 2 seconds
            setTimeout(() => {
                setEmail("");
                setSubject("");
                setMessage("");
                setSent(false);
            }, 2000);
        } catch (error: any) {
            console.error("Error sending email:", error);
            toast({
                title: "ส่งอีเมล์ไม่สำเร็จ",
                description: error.message || "เกิดข้อผิดพลาด กรุณาลองใหม่",
                variant: "destructive",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Card variant="glass">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    ส่งอีเมล์แจ้งลูกค้า
                </CardTitle>
                <CardDescription>
                    ส่งอีเมล์แจ้งข้อมูลหรือข่าวสารให้ลูกค้าโดยตรง
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {sent ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                            <CheckCircle2 className="h-8 w-8 text-green-500" />
                        </div>
                        <h3 className="text-lg font-medium">ส่งอีเมล์สำเร็จ!</h3>
                        <p className="text-muted-foreground">อีเมล์ถูกส่งไปยัง {email} แล้ว</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-2">
                            <Label htmlFor="email">อีเมล์ผู้รับ</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="customer@example.com"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="subject">หัวข้อ</Label>
                            <Input
                                id="subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="เช่น แจ้งเตือนการชำระเงิน, ติดตามออเดอร์"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="message">ข้อความ</Label>
                            <Textarea
                                id="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="พิมพ์ข้อความที่ต้องการส่งให้ลูกค้า..."
                                rows={8}
                            />
                            <p className="text-xs text-muted-foreground">
                                ข้อความจะถูกส่งในรูปแบบ HTML พร้อมหัวและท้ายอีเมล์อัตโนมัติ
                            </p>
                        </div>

                        <Button
                            onClick={handleSend}
                            disabled={sending || !email || !subject || !message}
                            className="w-full"
                        >
                            {sending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4 mr-2" />
                            )}
                            ส่งอีเมล์
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default SimpleEmailSend;
